"use client"

import { useState, useCallback } from "react"
import { Upload, X, FileType, Palette } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog"
import { Button } from "../../../components/ui/button"
import { Input } from "../../../components/ui/input"
import { Textarea } from "../../../components/ui/textarea"
import { Field, FieldLabel } from "../../../components/ui/field"
import { Spinner } from "../../../components/ui/spinner"
import type { ColorTable, ExtractedColor } from "../../../lib/types"
import { extractColorsFromSVG, getContrastColor, generateId } from "../../../lib/svg-color-extractor";
import { prepareColorTableForSave } from "../../../lib/color-table-service"

interface ColorTableModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSave: (colorTable: ColorTable) => void
}

export function ColorTableModal({ open, onOpenChange, onSave }: ColorTableModalProps) {
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [colors, setColors] = useState<ExtractedColor[]>([])
  const [svgContent, setSvgContent] = useState<string>("")
  const [fileName, setFileName] = useState<string>("")
  const [isLoading, setIsLoading] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [error, setError] = useState<string>("")

  const resetForm = useCallback(() => {
    setName("")
    setDescription("")
    setColors([])
    setSvgContent("")
    setFileName("")
    setError("")
  }, [])

  const handleFileSelect = useCallback(async (file: File) => {
    if (!file.name.toLowerCase().endsWith(".svg")) {
      setError("Por favor, selecione um arquivo SVG válido.")
      return
    }

    setIsLoading(true)
    setError("")

    try {
      const content = await file.text()
      //setSvgContent(content)
      setFileName(file.name)

      const extractedColors = extractColorsFromSVG(content)
      setColors(extractedColors)

      // Auto-preencher nome se estiver vazio
      if (!name) {
        setName(file.name.replace(".svg", "").replace(/[-_]/g, " "))
      }
    } catch {
      setError("Erro ao processar o arquivo SVG.")
    } finally {
      setIsLoading(false)
    }
  }, [name])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragging(false)

      const file = e.dataTransfer.files[0]
      if (file) {
        handleFileSelect(file)
      }
    },
    [handleFileSelect]
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) {
        handleFileSelect(file)
      }
    },
    [handleFileSelect]
  )

  const removeColor = useCallback((hexToRemove: string) => {
    setColors((prev) => prev.filter((c) => c.hex !== hexToRemove))
  }, [])

  const handleSave = useCallback(() => {
    if (!name.trim()) {
      setError("Nome é obrigatório.")
      return
    }

    if (colors.length === 0) {
      setError("Faça upload de um SVG para extrair as cores.")
      return
    }

    const colorTable = prepareColorTableForSave({
      name: name.trim(),
      description: description.trim() || undefined,
      colors,
      svgContent: svgContent || undefined,
    })

    onSave(colorTable)
    resetForm()
    onOpenChange(false)
  }, [name, description, colors, svgContent, onSave, onOpenChange, resetForm])

  const handleClose = useCallback(() => {
    resetForm()
    onOpenChange(false)
  }, [onOpenChange, resetForm])

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Palette className="size-5 text-primary" />
            Nova Tabela de Cores
          </DialogTitle>
          <DialogDescription>
            Faça upload de um arquivo SVG para extrair automaticamente as cores.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 py-4">
          {/* Upload Area */}
          <div
            className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              isDragging
                ? "border-primary bg-primary/5"
                : "border-border hover:border-primary/50"
            }`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <input
              type="file"
              accept=".svg"
              onChange={handleFileInputChange}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />

            {isLoading ? (
              <div className="flex flex-col items-center gap-3">
                <Spinner className="size-8" />
                <p className="text-sm text-muted-foreground">
                  Processando SVG...
                </p>
              </div>
            ) : fileName ? (
              <div className="flex flex-col items-center gap-3">
                <div className="flex items-center gap-2 text-primary">
                  <FileType className="size-8" />
                </div>
                <p className="text-sm font-medium">{fileName}</p>
                <p className="text-xs text-muted-foreground">
                  {colors.length} cores encontradas
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <Upload className="size-10 text-muted-foreground" />
                <div>
                  <p className="font-medium">Arraste um arquivo SVG aqui</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    ou clique para selecionar
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Extracted Colors Preview */}
          {colors.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm font-medium">Cores extraídas ({colors.length})</p>
              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 bg-secondary/50 rounded-lg">
                {colors.map((color) => (
                  <div
                    key={color.hex}
                    className="group relative flex items-center gap-2 px-2 py-1 bg-card rounded-md border"
                  >
                    <div
                      className="size-5 rounded border border-border/50"
                      style={{ backgroundColor: color.hex }}
                    />
                    <span className="text-xs font-mono">{color.hex}</span>
                    <span className="text-xs text-muted-foreground">
                      ({color.count})
                    </span>
                    <button
                      type="button"
                      onClick={() => removeColor(color.hex)}
                      className="absolute -top-1 -right-1 size-4 bg-destructive text-destructive-foreground rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                    >
                      <X className="size-2.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Form Fields */}
          <div className="grid gap-4">
            <Field>
              <FieldLabel htmlFor="name">Nome da tabela *</FieldLabel>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Paleta Principal"
              />
            </Field>

            <Field>
              <FieldLabel htmlFor="description">Descrição</FieldLabel>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Descrição opcional da tabela de cores..."
                rows={2}
              />
            </Field>
          </div>

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={isLoading || colors.length === 0}>
            {isLoading ? <Spinner className="mr-2" /> : null}
            Salvar Tabela
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}